import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn.js";

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;

/**
 * A styled native `<select>` — full keyboard/a11y/mobile behavior for free, no extra dependency.
 * Reach for a Radix-based combobox instead only once a screen needs search-as-you-type over a
 * long option list (e.g. `AsyncSelect` in `07_UI_UX_SPEC.md` §5) — none of Phase 01's enum
 * pickers (school status, institution type, ...) need that.
 */
/** Text of the first `<option value="">` placeholder ("All statuses", "Select a campus…"), if any. */
function placeholderText(children: React.ReactNode): string | undefined {
  for (const child of React.Children.toArray(children)) {
    if (
      React.isValidElement<{ value?: unknown; children?: React.ReactNode }>(child) &&
      child.type === "option" &&
      (child.props.value === "" || child.props.value === undefined)
    ) {
      return typeof child.props.children === "string" ? child.props.children : undefined;
    }
  }
  return undefined;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ className, children, ...props }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      // A filter `<select>` often has no visible `<Label>` — its placeholder option ("All
      // statuses") is the name a screen reader should announce (Phase 21 accessibility sweep).
      // An explicit `aria-label`, `aria-labelledby` or an `id` tied to a `<Label>` always wins.
      {...(!props["aria-label"] && !props["aria-labelledby"] && !props.id ? { "aria-label": placeholderText(children) } : {})}
      className={cn(
        "flex h-9 w-full appearance-none rounded-[var(--radius-control)] border border-input bg-surface px-3 pr-8 text-sm text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
  </div>
));
Select.displayName = "Select";
